import type { JSX, ReactNode } from 'react';
import { IconLock } from '@tabler/icons-react';
import { Navigate } from 'react-router-dom';
import { Alert, Box } from '@mantine/core';
import { CenteredLoader, Page } from '../components';
import { ROUTES } from '../config';
import { useAuthorization } from '../hooks';
import type { TCapability, TRole } from './roles';

interface IRequireRoleProps {
  /** Allow only these exact roles. */
  roles?: TRole[];
  /** Allow any role holding this capability. */
  capability?: TCapability;
  /** Allow any role ranking at least this high. */
  minRole?: TRole;
  /** Rendered when the user is authenticated but unauthorized. Defaults to an inline panel. */
  fallback?: ReactNode;
  children: ReactNode;
}

/**
 * Route/section guard (the React equivalent of a "decorator"). This is UX only —
 * real enforcement lives in Cloud Functions + Firestore rules. While auth is
 * resolving it shows a loader; logged-out users are redirected home; logged-in
 * but unauthorized users see an inline "Not authorized" panel (honest for
 * someone who typed the URL).
 */
function RequireRole({
  roles,
  capability,
  minRole,
  fallback,
  children,
}: Readonly<IRequireRoleProps>): JSX.Element {
  const { isLoading, isAuthenticated, hasAnyRole, can, isAtLeast } = useAuthorization();

  if (isLoading) {
    return <CenteredLoader size="lg" />;
  }

  if (!isAuthenticated) {
    return <Navigate to={ROUTES.LANDING} replace />;
  }

  const authorized =
    (roles ? hasAnyRole(roles) : true) &&
    (capability ? can(capability) : true) &&
    (minRole ? isAtLeast(minRole) : true);

  if (!authorized) {
    if (fallback !== undefined) {
      return <>{fallback}</>;
    }
    return (
      <Page title="Not authorized">
        <Box maw={520}>
          <Alert
            color="red"
            variant="light"
            icon={<IconLock size={18} />}
            title="Access restricted"
          >
            You don&apos;t have permission to view this page. If you believe this is a mistake,
            contact an administrator.
          </Alert>
        </Box>
      </Page>
    );
  }

  return <>{children}</>;
}

export default RequireRole;
